import { Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DocumentPrintEngineService } from 'app/core/document-print/document-print-engine.service';
import { OverlayLoaderDirective } from 'app/core/directives/overlay-loader.directive';
import { unwrapApiPayload } from 'app/core/models/document-print.models';
import { SalesInvoicesService } from 'app/modules/sales/sales-invoices/sales-invoices.service';
import { SalesInvoiceResponse } from 'app/modules/sales/sales.models';
import { Observable, finalize, from, map, switchMap } from 'rxjs';
import { ScbApiService } from '../scb-api.service';
import { ScbMyBillingPendingInvoiceDetail } from '../scb.models';
import { extractScbData } from '../scb-ui.utils';
import {
  mapScbPremiumInvoiceVm,
  mapScbPremiumInvoiceVmFromInvoice,
} from './premium-invoice/scb-premium-invoice.mapper';
import { ScbPremiumInvoiceVm } from './premium-invoice/scb-premium-invoice.models';

/** `my-billing` = tenant pending API; `sales-invoice` = platform sales invoice by id. */
export type ScbPremiumInvoiceLoadSource = 'my-billing' | 'sales-invoice';

export type ScbTenantInvoicePdfDialogData = {
  salesInvoiceId: string;
  titleHint?: string | null;
  /** Defaults to `my-billing` for tenant banner; payment proofs use `sales-invoice`. */
  source?: ScbPremiumInvoiceLoadSource;
};

@Component({
  selector: 'app-scb-tenant-invoice-pdf-dialog',
  standalone: true,
  imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogContent,
    MatIconModule,
    OverlayLoaderDirective,
  ],
  templateUrl: './scb-tenant-invoice-pdf-dialog.component.html',
  styles: `
    :host {
      display: block;
    }
    ::ng-deep .mat-mdc-dialog-container .mdc-dialog__surface {
      padding: 0 !important;
    }
    .scb-tenant-pdf-iframe {
      width: 100%;
      height: min(70vh, 720px);
      border: 0;
      border-radius: 0.75rem;
      background: #f8fafc;
    }
  `,
})
export class ScbTenantInvoicePdfDialogComponent implements OnInit, OnDestroy {
  private readonly api = inject(ScbApiService);
  private readonly salesInvoices = inject(SalesInvoicesService);
  private readonly documentPrint = inject(DocumentPrintEngineService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(MatDialogRef<ScbTenantInvoicePdfDialogComponent>);
  private readonly data = inject<ScbTenantInvoicePdfDialogData>(MAT_DIALOG_DATA);

  private blobUrl: string | null = null;
  private downloadFileName = 'BizCare_Premium_Invoice.pdf';

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly pdfViewerUrl = signal<SafeResourceUrl | null>(null);
  readonly title = signal('Invoice');

  ngOnInit(): void {
    const id = this.data?.salesInvoiceId?.trim();
    if (!id) {
      this.loading.set(false);
      this.error.set('Invoice id is missing');
      return;
    }
    if (this.data.titleHint?.trim()) {
      this.title.set(this.data.titleHint.trim());
    }
    this.load(id);
  }

  ngOnDestroy(): void {
    this.revokeBlobUrl();
  }

  close(): void {
    this.dialogRef.close();
  }

  download(): void {
    if (!this.blobUrl) return;
    const a = document.createElement('a');
    a.href = this.blobUrl;
    a.download = this.downloadFileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  private load(salesInvoiceId: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.revokeBlobUrl();
    this.pdfViewerUrl.set(null);

    this.resolveModel(salesInvoiceId)
      .pipe(
        switchMap((model) => {
          const invoiceNumber = model.invoiceNumber?.trim() || this.data.titleHint || 'Invoice';
          this.title.set(String(invoiceNumber));
          const safeNumber = String(invoiceNumber)
            .trim()
            .replace(/[<>:"/\\|?*\u0000-\u001f]+/g, '-')
            .replace(/\s+/g, '_');
          this.downloadFileName = `BizCare_Premium_Invoice_${safeNumber || 'invoice'}.pdf`;
          return from(this.documentPrint.generateScbPremiumInvoicePdfBlob(model));
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (blob) => {
          this.blobUrl = URL.createObjectURL(blob);
          this.pdfViewerUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl));
        },
        error: (err: { error?: { message?: string }; message?: string }) => {
          this.error.set(
            err?.error?.message || err?.message || 'Unable to load invoice PDF'
          );
        },
      });
  }

  private resolveModel(salesInvoiceId: string): Observable<ScbPremiumInvoiceVm> {
    const source: ScbPremiumInvoiceLoadSource = this.data.source ?? 'my-billing';
    if (source === 'sales-invoice') {
      return this.salesInvoices.getSalesInvoice(salesInvoiceId).pipe(
        map((resp) => {
          const invoice = unwrapApiPayload(resp) as SalesInvoiceResponse | null;
          if (!invoice) {
            throw new Error('Unable to load invoice');
          }
          return mapScbPremiumInvoiceVmFromInvoice(invoice);
        })
      );
    }

    return this.api.getMyBillingPendingInvoiceDetail(salesInvoiceId).pipe(
      map((resp) => {
        const detail = extractScbData<ScbMyBillingPendingInvoiceDetail>(resp);
        if (!detail) {
          throw new Error(resp?.message || 'Unable to load invoice');
        }
        return mapScbPremiumInvoiceVm(detail);
      })
    );
  }

  private revokeBlobUrl(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }
}
